import { defineStore } from 'pinia'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { ORDER_REGISTER_NOTICE_POPUP_SESSION_KEY } from '@/utils/noticePopupSession'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    token: localStorage.getItem('token') || null,
    isAuthenticated: !!localStorage.getItem('token')
  }),

  actions: {
    async login(credentials) {
      try {
        const response = await axios.post('/api/auth/login', credentials)
        const { token, user } = response.data
        
        this.token = token
        this.user = user
        this.isAuthenticated = true
        
        localStorage.setItem('token', token)
        localStorage.setItem('user', JSON.stringify(user))
        
        // 로그인 시간 저장 (항상 로컬 시간 기준 오늘 날짜로 설정)
        // 서버에서 받은 날짜가 있더라도 로컬 시간 기준 오늘 날짜로 설정하여 세션 만료 문제 방지
        const now = new Date()
        const year = now.getFullYear()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const day = String(now.getDate()).padStart(2, '0')
        const loginDate = `${year}-${month}-${day}` // YYYY-MM-DD 형식
        localStorage.setItem('loginDate', loginDate)

        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem(ORDER_REGISTER_NOTICE_POPUP_SESSION_KEY)
        }
        
        // axios 기본 헤더 설정
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
        
        return { success: true }
      } catch (error) {
        return { 
          success: false, 
          message: error.response?.data?.message || '로그인에 실패했습니다' 
        }
      }
    },

    async loginAsUser(userId) {
      try {
        const response = await axios.post('/api/auth/login-as', { userId })
        const { token, user } = response.data

        this.token = token
        this.user = user
        this.isAuthenticated = true

        localStorage.setItem('token', token)
        localStorage.setItem('user', JSON.stringify(user))

        const now = new Date()
        const year = now.getFullYear()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const day = String(now.getDate()).padStart(2, '0')
        const loginDate = `${year}-${month}-${day}`
        localStorage.setItem('loginDate', loginDate)

        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem(ORDER_REGISTER_NOTICE_POPUP_SESSION_KEY)
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

        return { success: true, user }
      } catch (error) {
        return {
          success: false,
          message: error.response?.data?.message || '로그인에 실패했습니다'
        }
      }
    },

    async logout() {
      this.user = null
      this.token = null
      this.isAuthenticated = false
      
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('loginDate')

      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(ORDER_REGISTER_NOTICE_POPUP_SESSION_KEY)
      }
      
      delete axios.defaults.headers.common['Authorization']
    },

    async verifyToken() {
      if (!this.token) return false
      
      try {
        const response = await axios.get('/api/auth/verify')
        this.user = response.data.user
        return true
      } catch (error) {
        this.logout()
        return false
      }
    },

    initializeAuth() {
      const token = localStorage.getItem('token')
      const user = localStorage.getItem('user')
      const loginDate = localStorage.getItem('loginDate')
      
      if (token && user) {
        // 로그인 날짜 확인 (로컬 시간 기준)
        const now = new Date()
        const year = now.getFullYear()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const day = String(now.getDate()).padStart(2, '0')
        const today = `${year}-${month}-${day}` // YYYY-MM-DD 형식
        
        if (loginDate && loginDate !== today) {
          // 오늘이 아니면 로그아웃
          console.log(`[initializeAuth] 로그인 날짜 불일치: ${loginDate} !== ${today}`)
          this.logout()
          return false
        }
        
        this.token = token
        this.user = JSON.parse(user)
        this.isAuthenticated = true
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
        
        // 로그인 날짜가 없으면 오늘로 설정 (기존 사용자 호환성)
        if (!loginDate) {
          localStorage.setItem('loginDate', today)
        }
      }
      
      return true
    },
    
    // 로그인 날짜가 오늘인지 확인
    isLoginToday() {
      const loginDate = localStorage.getItem('loginDate')
      if (!loginDate) {
        console.log('[isLoginToday] loginDate가 없습니다')
        return false
      }
      
      // 로컬 시간 기준으로 오늘 날짜 계산 (ISO 형식은 UTC 기준이므로 로컬 시간 사용)
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const today = `${year}-${month}-${day}` // YYYY-MM-DD 형식
      
      // 테스트용: 로그인 날짜와 현재 날짜 표시 (주석 처리됨)
      // ElMessage.info(`로그인 날짜: ${loginDate}, 현재 날짜: ${today}`)
      console.log(`[isLoginToday] loginDate: ${loginDate}, today: ${today}, 일치: ${loginDate === today}`)
      
      return loginDate === today
    }
  }
})
